// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { createOptionsLoader } from 'common/components/Select/helpers';
import {
    IUserProjectsListFilters,
    users,
} from 'common/api/resources/User';
import { IProjectResponse } from 'common/api/resources/Project';
import { ISelectOption } from 'common/components';

export interface IProjectSelectOption extends ISelectOption {
    value: number;
    item: IProjectResponse;
}

export const projectToOption = (project: IProjectResponse): IProjectSelectOption => ({
    label: project.name,
    value: project.id,
    item: project,
});

export const createUserProjectsOptionsLoader = (userId: number, filters?: IUserProjectsListFilters) =>
    createOptionsLoader(
        (params) => users.projects.list(userId, params),
        projectToOption,
        userId > 0,
        filters
    );

export const fetchUserDefaultProjectOption = async (
    userId: number,
    filters?: IUserProjectsListFilters
): Promise<IProjectSelectOption | undefined> => {
    if (userId <= 0) {
        return undefined;
    }

    const { data: { data } } = await users.projects.list(userId, {
        filters: {
            ...filters,
            is_default: true,
        },
        per_page: 1,
    });

    return data[0] ? projectToOption(data[0]) : undefined;
};

/**
 * Loads a user's default project while guarding against stale responses when the
 * target user changes again before a previous fetch resolves. Generic over `T` so
 * callers that only have a partial project shape available up front (e.g. restoring
 * a backup's short project reference) can still set an initial selection.
 */
export const useUserDefaultProjectLoader = <T extends ISelectOption = IProjectSelectOption>(initialOption?: T) => {
    const [selectedProjectOption, setSelectedProjectOption] = React.useState<T | undefined>(initialOption);
    const [isLoadingDefaultProject, setIsLoadingDefaultProject] = React.useState(false);
    const latestUserIdRef = React.useRef<number>();

    const loadDefaultProject = React.useCallback((
        userId: number,
        filters?: IUserProjectsListFilters,
        onLoaded?: (option: IProjectSelectOption) => void
    ) => {
        latestUserIdRef.current = userId;
        setIsLoadingDefaultProject(true);

        return fetchUserDefaultProjectOption(userId, filters).then((option) => {
            if (latestUserIdRef.current !== userId) {
                return;
            }

            if (option) {
                setSelectedProjectOption(option as unknown as T);
                onLoaded?.(option);
            }
        }).finally(() => {
            if (latestUserIdRef.current === userId) {
                setIsLoadingDefaultProject(false);
            }
        });
    }, []);

    return {
        selectedProjectOption,
        setSelectedProjectOption,
        isLoadingDefaultProject,
        loadDefaultProject,
    };
};
